/**
 * @graphql cc
 */
import React, { useCallback, useRef, useState } from 'react';

import { token } from '@atlaskit/tokens';
import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Flex } from '@atlaskit/primitives/compiled/flex';

import {
	useObjectCommentDispatchContext,
	useObjectCommentContext,
} from '@confluence/comment-context/entry-points/ObjectCommentContext.ts';
import { AddFooterCommentMode } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { VIEW_PAGE_LABELS_IN_FOOTER_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceStart } from '@confluence/experience-tracker/entry-points/component.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { LoadablePaint } from '@confluence/loadable';
import { usePageContentId } from '@confluence/page-context';
import { ProfileAvatar } from '@confluence/profile/entry-points/ProfileAvatarComponent.ts';

import { ObjectAddCommentPrompt } from './ObjectAddCommentPrompt.tsx';
import { useCommentAuthorInfo } from './ObjectAddCommentProfileWrapper.tsx';
import { ObjectCommentPageReactions } from './ObjectCommentPageReactions.tsx';

// Same `webpackChunkName` as `ObjectCommentContainer`'s own copy of this loader, so no extra
// chunk is produced even though each file defines it separately.
const FooterLabelsLoader = LoadablePaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-FooterLabels" */ '@confluence/labels-footer'))
			.FooterLabels,
});

const styles = cssMap({
	addCommentContainerStyles: {
		position: 'sticky',
		bottom: token('space.0'),
		display: 'flex',
		width: '100%',
		padding: token('space.200'),
		borderTop: `${token('border.width')} solid ${token('color.border')}`,
		backgroundColor: token('elevation.surface'),
	},
	addCommentClosedContainerStyles: {
		width: '100%',
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		flexShrink: 0,
	},
	// `minWidth: 0` lets the trigger shrink next to the fixed-width reactions cluster — a flex
	// item's default `min-width: auto` would otherwise refuse to shrink below its content size.
	addCommentLeftGroupStyles: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		flex: '1 1 auto',
		minWidth: 0,
	},
	// New layout: the avatar and the trigger (which has its own minimum width) never shrink below
	// their content, so a wide reactions cluster gives way instead — it is what holds `minWidth: 0`.
	addCommentLeftGroupNewStyles: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		flex: '1 1 auto',
		minWidth: 'min-content',
	},
	addCommentReactionsContainerStyles: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-end',
	},
	// Explicit `width: '100%'` so `justify-content: flex-end` has room to push pills/trigger right
	// once the reactions row expands and its Flex ancestor stretches to fill the column.
	addCommentReactionsContainerNewStyles: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-end',
		width: '100%',
		minWidth: 0,
	},
	rightButtonContainerStyles: {
		alignItems: 'center',
	},
	// Shrinks to whatever room is left after the comment trigger's minimum width. `Reactions`
	// sizes its collapsed row to the pills that fit in that room, so the cluster never wraps
	// or overflows. `minWidth: 0` lets this flex item go below its content size.
	rightButtonContainerNewStyles: {
		alignItems: 'center',
		flex: '0 1 auto',
		minWidth: 0,
		flexWrap: 'nowrap',
	},
	addCommentClosedContainerNewStyles: {
		width: '100%',
		display: 'flex',
		flexWrap: 'nowrap',
		alignItems: 'center',
		gap: token('space.300'),
		flexShrink: 0,
	},
	// Applied instead of the base style above, driven by an explicit `reactionsExpanded` prop.
	addCommentClosedContainerNewExpandedStyles: {
		width: '100%',
		display: 'flex',
		flexDirection: 'column-reverse',
		alignItems: 'stretch',
		gap: token('space.300'),
		flexShrink: 0,
	},
	// 28px matches the rendered `size="small"` `ProfileAvatar` (including its hover ring), so this
	// wrapper doesn't stretch or shrink next to the input trigger.
	newAddCommentAvatarStyles: {
		width: '28px',
		height: '28px',
		flexShrink: 0,
	},
});

type NewAddCommentPromptContentProps = {
	showReactions: boolean;
	isOffline?: boolean;
	optimisticReactions?: boolean;
	onExpand: () => void;
	autofocusRef: React.MutableRefObject<boolean>;
};

// Separate component (not a gate branch inside the master JSX below), so `useCommentAuthorInfo`'s
// query only runs when this layout is the one actually rendering.
const NewAddCommentPromptContent = ({
	showReactions,
	isOffline,
	optimisticReactions,
	onExpand,
	autofocusRef,
}: NewAddCommentPromptContentProps): React.JSX.Element => {
	const authorInfo = useCommentAuthorInfo();
	const [reactionsExpanded, setReactionsExpanded] = useState(false);

	return (
		<Box
			xcss={
				reactionsExpanded
					? styles.addCommentClosedContainerNewExpandedStyles
					: styles.addCommentClosedContainerNewStyles
			}
		>
			<Box xcss={styles.addCommentLeftGroupNewStyles}>
				<Box xcss={styles.newAddCommentAvatarStyles}>
					{/* Withhold the avatar while the fallback query is in flight or failed, rather than
					rendering `ProfileAvatar` with an empty `src` (which shows its generic placeholder image). */}
					{!authorInfo.loading && !authorInfo.error && (
						<ProfileAvatar
							size="small"
							canViewProfile
							src={authorInfo.avatarUrl}
							userId={authorInfo.accountId}
							isAnonymous={!authorInfo.accountId}
							name={authorInfo.displayName}
							testId="object-add-comment-prompt-avatar"
						/>
					)}
				</Box>
				<ObjectAddCommentPrompt onClick={onExpand} autofocusRef={autofocusRef} />
			</Box>
			{showReactions && (
				<Flex
					gap="space.050"
					testId="objectAddCommentContainer-rightButtonContainer"
					xcss={styles.rightButtonContainerNewStyles}
				>
					<Box xcss={styles.addCommentReactionsContainerNewStyles}>
						<ObjectCommentPageReactions
							isOffline={isOffline}
							optimisticReactions={optimisticReactions}
							onExpandedChange={setReactionsExpanded}
						/>
					</Box>
				</Flex>
			)}
		</Box>
	);
};

export const ObjectAddCommentContainerComponent = ({
	showReactions,
	showLabels,
	isOffline,
	optimisticReactions = false,
}: {
	showReactions: boolean;
	showLabels: boolean;
	isOffline?: boolean;
	optimisticReactions?: boolean;
}): React.JSX.Element => {
	const shouldAutofocusPromptRef = useRef<boolean>(false);
	const { expandCommentContainer, setFooterEditorEntry } = useObjectCommentDispatchContext();
	const { expanded } = useObjectCommentContext();

	const [contentId] = usePageContentId();

	const objectAddCommentContainerId = 'addCommentButton';

	const handleExpandCommentContainer = useCallback(() => {
		expandCommentContainer({
			entryPoint: 'pageFooter',
		});
		setFooterEditorEntry(AddFooterCommentMode.FOOTER);
	}, [expandCommentContainer, setFooterEditorEntry]);

	return (
		<Box
			id={objectAddCommentContainerId}
			xcss={styles.addCommentContainerStyles}
			testId={objectAddCommentContainerId}
		>
			{!expanded &&
				(expValEquals('confluence_footer_reactions_redesign', 'isEnabled', true) ? (
					<NewAddCommentPromptContent
						showReactions={showReactions}
						isOffline={isOffline}
						optimisticReactions={optimisticReactions}
						onExpand={handleExpandCommentContainer}
						autofocusRef={shouldAutofocusPromptRef}
					/>
				) : (
					<Box xcss={styles.addCommentClosedContainerStyles}>
						<ObjectAddCommentPrompt
							onClick={handleExpandCommentContainer}
							autofocusRef={shouldAutofocusPromptRef}
						/>
						<Flex
							gap="space.050"
							testId="objectAddCommentContainer-rightButtonContainer"
							xcss={styles.rightButtonContainerStyles}
						>
							{showLabels && (
								<>
									<ExperienceStart id={contentId} name={VIEW_PAGE_LABELS_IN_FOOTER_EXPERIENCE} />
									<FooterLabelsLoader />
								</>
							)}
							{showReactions && (
								<Box xcss={styles.addCommentReactionsContainerStyles}>
									<ObjectCommentPageReactions
										isOffline={isOffline}
										optimisticReactions={optimisticReactions}
									/>
								</Box>
							)}
						</Flex>
					</Box>
				))}
		</Box>
	);
};

export const ObjectAddCommentContainer: React.FC<{
	showReactions: boolean;
	showLabels: boolean;
	isOffline?: boolean;
	optimisticReactions?: boolean;
}> = withErrorBoundary({
	attribution: Attribution.COMMENTS,
})(ObjectAddCommentContainerComponent);
